import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Image, OverlayTrigger, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import {
  faArrowCircleLeft,
  faCheckCircle,
  faEllipsisV,
  faFileExcel,
  faFileExport,
  faFileImport,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import { useDebounce } from 'use-debounce';

import AllProductsReceivedModal from 'components/data_sources/single_data_source_view/AllProductsReceivedModal';
import { backgroundGrey1, greyBackground, greyBorder, white } from 'lib/css/colors';
import Container from 'components/shared/Container';
import {
  DATA_SOURCE_CONNECTED_MANUFACTURERS,
  DATA_SOURCE_EXPORT_GDSN_MANUFACTURER_ATTRIBUTES,
  DATA_SOURCE_EXPORT_GDSN_MANUFACTURER_PRODUCTS,
  DATA_SOURCE_REIMPORT_FROM_S3_XML,
  DATA_SOURCE_SEND_GDSN_MANUFACTURER_PRODUCTS_TO_CND,
  GLOBAL_MANUFACTURERS_UPDATE_FROM_FSA,
} from 'lib/networking/endpoints';
import DataSourceProductsTab from 'components/data_sources/single_data_source_view/DataSourceProductsTab';
import GDSNProcessManufacturerModal from 'components/data_sources/single_data_source_view/GDSNProcessManufacturerModal';
import GDSNReimportFromS3Modal from 'components/data_sources/single_data_source_view/GDSNReimportFromS3Modal';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import pointerOnHover from 'lib/css/pointerOnHover';
import SendGDSNManufacturerProductsModal from 'components/data_sources/single_data_source_view/SendGDSNManufacturerProductsModal';
import SwalDialog, { SwalToast, exportFileErrorSwal, exportFileSuccessSwal } from 'lib/utils/toast';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

function GDSNDataSourceManufacturersTab({ dataSource, processDataSource, processing, refetchRun }) {
  const hasUserPermissions = useUserPermissions();
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processingManufacturer, setProcessingManufacturer] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchFilter] = useDebounce(searchInput, 400);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [productSearchInput, setProductSearchInput] = useState('');
  const [productSearchFilter] = useDebounce(productSearchInput, 400);

  // State for All Products Received modal
  const [showAllProductsModal, setShowAllProductsModal] = useState(false);
  const [selectedManufacturerForUpdate, setSelectedManufacturerForUpdate] = useState(null);

  // State for Send to C&D modal
  const [showSendToCNDModal, setShowSendToCNDModal] = useState(false);
  const [manufacturerForCND, setManufacturerForCND] = useState(null);

  // State for Re-import from S3 XML modal
  const [showReimportModal, setShowReimportModal] = useState(false);
  const [manufacturerForReimport, setManufacturerForReimport] = useState(null);

  const {
    data: { data: manufacturers = [], count = 0, total_product_count = 0 } = {},
    loading,
    refetch,
  } = useGet(DATA_SOURCE_CONNECTED_MANUFACTURERS, {
    data_source_id: dataSource.id,
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
    query: searchFilter,
  });

  const { postData: exportProducts, loading: exportingProducts } = usePost(
    DATA_SOURCE_EXPORT_GDSN_MANUFACTURER_PRODUCTS,
    () => {
      Swal.fire({
        icon: 'success',
        html: `
        <div>
          The export CSV file will be generated shortly.
          You can download the generated file from<br>
          <a
            href="/file-exports"
            rel="noopener noreferrer"
            target="_blank"
          >
            File Exports Page
          </a>
        </div>`,
        title: 'Export Initiated',
        position: 'center',
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        text: `An error occurred while exporting products: ${error?.response?.data?.message}`,
        title: 'Export Error',
      }),
  );

  const { postData: updateManufacturer, loading: updatingManufacturer } = usePost(
    GLOBAL_MANUFACTURERS_UPDATE_FROM_FSA,
    () => {
      Swal.fire({
        icon: 'success',
        text: 'Manufacturer updated successfully',
        title: 'Success',
        position: 'center',
        timer: 2000,
      });
      refetchRun();
      refetch();
    },
    error =>
      Swal.fire({
        icon: 'error',
        text: `An error occurred while updating manufacturer: ${error?.response?.data?.message}`,
        title: 'Update Error',
      }),
  );

  const { postData: sendProductsToCND, loading: sendingProductsToCND } = usePost(
    DATA_SOURCE_SEND_GDSN_MANUFACTURER_PRODUCTS_TO_CND,
    () => {
      Swal.fire({
        icon: 'success',
        text: 'GDSN products are being sent to C&D',
        title: 'Success',
        position: 'center',
        timer: 2000,
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        text: `An error occurred while sending products: ${error?.response?.data?.message}`,
        title: 'Send Error',
      }),
  );

  const { postData: reimportFromS3, loading: reimporting } = usePost(
    DATA_SOURCE_REIMPORT_FROM_S3_XML,
    () => SwalToast('success', 'Re-import from S3 XML has been queued'),
    error =>
      SwalDialog(
        'error',
        `An error occurred while queuing the re-import: ${error?.response?.data?.message}`,
        'Re-import Error',
        'center',
      ),
  );

  const { postData: exportAttributes, loading: exportingAttributes } = usePost(
    DATA_SOURCE_EXPORT_GDSN_MANUFACTURER_ATTRIBUTES,
    () => exportFileSuccessSwal('Export started. The file will appear on the File Exports page.'),
    error =>
      exportFileErrorSwal(
        `An error occurred while starting the export: ${error?.response?.data?.message}`,
      ),
  );

  const handleProcessClick = (e, manufacturer) => {
    e.stopPropagation();
    setProcessingManufacturer(manufacturer);
    setShowProcessModal(true);
  };

  const handleExportAttributes = (e, manufacturer) => {
    e.stopPropagation();
    exportAttributes({
      data_source_id: dataSource.id,
      manufacturer_id: manufacturer.id,
    });
  };

  const handleReimportClick = (e, manufacturer) => {
    e.stopPropagation();
    setManufacturerForReimport(manufacturer);
    setShowReimportModal(true);
  };

  const handleConfirmReimport = (manufacturerId, manufacturerGlns, excludeWithdrawn) => {
    reimportFromS3({
      data_source_id: dataSource.id,
      manufacturer_id: manufacturerId,
      manufacturer_glns: manufacturerGlns,
      exclude_withdrawn: excludeWithdrawn,
    }).then(() => {
      setTimeout(() => {
        refetchRun();
      }, 1000);
    });
    setShowReimportModal(false);
    setManufacturerForReimport(null);
  };

  function handleProcessNow(manufacturerId, manufacturerGlns, lookbackDays) {
    processDataSource({
      data_source_id: dataSource.id,
      manufacturer_id: manufacturerId,
      manufacturer_glns: manufacturerGlns,
      lookback_days: lookbackDays,
      process_delta_file: false,
    }).then(() => {
      setTimeout(() => {
        refetchRun();
      }, 1000);
    });
    setShowProcessModal(false);
    setProcessingManufacturer(null);
  }

  const handleExportProducts = () => {
    if (!selectedManufacturer) return;

    exportProducts({
      data_source_id: dataSource.id,
      manufacturer_id: selectedManufacturer.id,
      search: productSearchFilter || undefined,
    });
  };

  const handleUpdateProductsStatus = (e, manufacturer) => {
    e.stopPropagation();
    setSelectedManufacturerForUpdate(manufacturer);
    setShowAllProductsModal(true);
  };

  const handleSaveProductsStatus = (manufacturerId, data) => {
    updateManufacturer({
      id: manufacturerId,
      ...data,
    });
  };

  const handleSendProductsToCND = (e, manufacturer) => {
    e.stopPropagation();
    setManufacturerForCND(manufacturer);
    setShowSendToCNDModal(true);
  };

  const handleConfirmSendToCND = archiveProducts => {
    if (!manufacturerForCND) return;

    sendProductsToCND({
      data_source_id: dataSource.id,
      manufacturer_id: manufacturerForCND.id,
      archive_products: archiveProducts,
    });
    setShowSendToCNDModal(false);
    setManufacturerForCND(null);
  };

  if (loading && !manufacturers.length) {
    return <LoadingSpinner />;
  }

  if (selectedManufacturer) {
    return (
      <Container>
        <div className="mb-3">
          <h4 className="font-weight-bold m-0 mb-4">
            <FontAwesomeIcon
              className={classNames(pointerOnHover, 'mr-2')}
              icon={faArrowCircleLeft}
              onClick={() => setSelectedManufacturer(null)}
            />
            Back to Manufacturers ({selectedManufacturer.name})
          </h4>
          <div className="d-flex align-items-center justify-content-between">
            <input
              type="text"
              className={classNames('form-control m-1', css(styles.searchInput))}
              placeholder="Search by SKU or GTIN..."
              value={productSearchInput}
              onChange={e => setProductSearchInput(e.target.value)}
            />
            {hasUserPermissions([UserPermission.EXPORT_PRODUCT_DATA]) && (
              <Button
                className="m-1"
                variant="outline-primary"
                size="sm"
                onClick={handleExportProducts}
                disabled={exportingProducts}
              >
                <FontAwesomeIcon icon={faFileExport} className="mr-1" />
                {exportingProducts ? 'Exporting...' : 'Export Products'}
              </Button>
            )}
          </div>
        </div>
        <DataSourceProductsTab
          dataSource={dataSource}
          manufacturerId={selectedManufacturer.id}
          searchFilter={productSearchFilter}
        />
      </Container>
    );
  }

  return (
    <Container>
      <GDSNProcessManufacturerModal
        show={showProcessModal}
        onHide={() => {
          setShowProcessModal(false);
          setProcessingManufacturer(null);
        }}
        manufacturer={processingManufacturer}
        dataSource={dataSource}
        onSubmit={handleProcessNow}
      />
      <AllProductsReceivedModal
        show={showAllProductsModal}
        onHide={() => {
          setShowAllProductsModal(false);
          setSelectedManufacturerForUpdate(null);
        }}
        manufacturer={selectedManufacturerForUpdate}
        onSave={handleSaveProductsStatus}
      />
      <SendGDSNManufacturerProductsModal
        show={showSendToCNDModal}
        onHide={() => {
          setShowSendToCNDModal(false);
          setManufacturerForCND(null);
        }}
        manufacturer={manufacturerForCND}
        onSubmit={handleConfirmSendToCND}
        loading={sendingProductsToCND}
      />
      <GDSNReimportFromS3Modal
        show={showReimportModal}
        onHide={() => {
          setShowReimportModal(false);
          setManufacturerForReimport(null);
        }}
        manufacturer={manufacturerForReimport}
        onConfirm={handleConfirmReimport}
        loading={reimporting}
      />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          {/* Stats Cards */}
          <div className="d-flex align-items-center mr-4">
            <div className="d-flex align-items-center bg-light rounded-pill px-3 py-2 mr-3">
              <i className="fas fa-industry text-primary mr-2"></i>
              <span className="font-weight-bold text-primary">{count}</span>
              <span className="text-muted ml-1">GDSN Partners</span>
            </div>
            <div className="d-flex align-items-center bg-light rounded-pill px-3 py-2 mr-3">
              <i className="fas fa-boxes text-success mr-2"></i>
              <span className="font-weight-bold text-success">{total_product_count}</span>
              <span className="text-muted ml-1">Total Products</span>
            </div>
          </div>

          <div className="position-relative">
            <input
              type="text"
              className={classNames('form-control', css(styles.searchInput))}
              placeholder="Search by manufacturer name..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>
        </div>

        <PaginationComponent
          totalItems={count}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead className={`table-grey ${css(styles.tableHeader)}`}>
            <tr>
              <th className={`text-center ${css(styles.logoColumn)}`}>
                <i className="fas fa-image me-1"></i>Logo
              </th>
              <th>
                <i className="fas fa-building me-1"></i>Manufacturer
              </th>
              <th>
                <i className="fas fa-barcode me-1"></i>GLN
              </th>
              <th className={`text-center ${css(styles.productCountColumn)}`}>
                <i className="fas fa-boxes me-1"></i>Products
              </th>
              <th className={`text-center ${css(styles.dateColumn)}`}>
                <i className="fas fa-clock me-1"></i>Last Updated
              </th>
              <th className={`text-center ${css(styles.actionsColumn)}`}>
                <i className="fas fa-cog me-1"></i>Actions
              </th>
            </tr>
          </thead>
          {!manufacturers.length ? (
            <tbody>
              <tr>
                <td colSpan="6" className="text-center py-5">
                  <div className="text-muted">
                    <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                    <h5 className="mb-2">No Manufacturers Found</h5>
                    <p className="mb-0">Have you executed the Data Source yet?</p>
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {manufacturers.map(manufacturer => (
                <tr
                  key={manufacturer.id}
                  onClick={() => setSelectedManufacturer(manufacturer)}
                  className={`${pointerOnHover} table-row-hover ${css(styles.tableRow)}`}
                >
                  <td className="text-center">
                    <div className="manufacturer-logo-container">
                      <Image
                        src={manufacturer.logo_url || placeholderProductImage}
                        className={css(styles.circle)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="manufacturer-info">
                      <div className="d-flex align-items-center">
                        <h6 className="mb-1 fw-bold text-primary me-2 mr-2">{manufacturer.name}</h6>
                        {manufacturer.all_products_received && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-${manufacturer.id}`}>
                                All products have been received from this manufacturer
                              </Tooltip>
                            }
                          >
                            <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                          </OverlayTrigger>
                        )}
                      </div>
                      <small className="text-muted">
                        <i className="fas fa-hashtag me-1"></i>ID: {manufacturer.id}
                      </small>
                    </div>
                  </td>
                  <td>
                    <div className="gln-container">
                      {manufacturer.manufacturer_glns &&
                      Object.keys(manufacturer.manufacturer_glns).length > 0 ? (
                        <div className="gln-list">
                          {Object.entries(manufacturer.manufacturer_glns).map(
                            ([gln, subCompanyName], index) => (
                              <div key={index} className="gln-item mb-1">
                                <span className="badge bg-light text-dark border me-1">
                                  <i className="fas fa-barcode me-1"></i>
                                  {gln}
                                </span>
                                {subCompanyName && (
                                  <small className="text-muted">({subCompanyName})</small>
                                )}
                              </div>
                            ),
                          )}
                        </div>
                      ) : (
                        <span className="text-muted fst-italic">
                          <i className="fas fa-minus-circle me-1"></i>No GLN
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-center">
                    <span
                      className={`badge px-3 py-2 ${greyBackground} ${css(
                        styles.productCountBadge,
                      )}`}
                    >
                      <i className="fas fa-box me-1"></i>
                      {manufacturer.product_count?.toLocaleString() || '0'}
                    </span>
                  </td>
                  <td className="text-center">
                    <div className="date-info">
                      <div className="fw-medium">
                        {manufacturer.date_modified
                          ? new Date(manufacturer.date_modified).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '-'}
                      </div>
                      {manufacturer.date_modified && (
                        <small className="text-muted">
                          {new Date(manufacturer.date_modified).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </small>
                      )}
                    </div>
                  </td>
                  <td className="text-center">
                    <div className="d-flex justify-content-center">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={e => handleProcessClick(e, manufacturer)}
                        disabled={processing}
                        className={`btn-process shadow-sm mr-2 ${css(styles.processButton)}`}
                      >
                        {processing ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Processing...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-play me-1"></i>
                            Process
                          </>
                        )}
                      </Button>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`tooltip-send-${manufacturer.id}`}>
                            Send products to C&D
                          </Tooltip>
                        }
                      >
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={e => handleSendProductsToCND(e, manufacturer)}
                          disabled={sendingProductsToCND}
                          className={`btn-process shadow-sm mr-2`}
                        >
                          <FontAwesomeIcon icon={faPaperPlane} />
                        </Button>
                      </OverlayTrigger>
                      {hasUserPermissions([UserPermission.MANAGE_DATA_SOURCES]) && (
                        <>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-reimport-${manufacturer.id}`}>
                                Re-import products from S3 XML
                              </Tooltip>
                            }
                          >
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={e => handleReimportClick(e, manufacturer)}
                              disabled={reimporting}
                              className="btn-process shadow-sm mr-2"
                            >
                              <FontAwesomeIcon icon={faFileImport} />
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-export-attrs-${manufacturer.id}`}>
                                Export products with attributes (Excel)
                              </Tooltip>
                            }
                          >
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={e => handleExportAttributes(e, manufacturer)}
                              disabled={exportingAttributes}
                              className="btn-process shadow-sm mr-2"
                            >
                              <FontAwesomeIcon icon={faFileExcel} />
                            </Button>
                          </OverlayTrigger>
                        </>
                      )}
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`tooltip-actions-${manufacturer.id}`}>
                            Update all products received status
                          </Tooltip>
                        }
                      >
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={e => handleUpdateProductsStatus(e, manufacturer)}
                          disabled={updatingManufacturer}
                          className={`btn-process shadow-sm`}
                        >
                          <FontAwesomeIcon icon={faEllipsisV} />
                        </Button>
                      </OverlayTrigger>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
    </Container>
  );
}

const styles = StyleSheet.create({
  searchInput: {
    width: '300px',
    paddingLeft: '35px',
  },
  circle: {
    borderRadius: '10%',
    width: '100%',
    height: '100%',
    backgroundColor: white,
    objectFit: 'contain',
  },
  logoColumn: {
    width: '80px',
  },
  productCountColumn: {
    width: '120px',
  },
  dateColumn: {
    width: '160px',
  },
  actionsColumn: {
    width: '180px',
  },
  tableRow: {
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  tableHeader: {
    backgroundColor: backgroundGrey1,
    borderBottom: `1px solid ${greyBorder}`,
  },
  productCountBadge: {
    fontSize: '0.9rem',
  },
});
GDSNDataSourceManufacturersTab.propTypes = {
  dataSource: PropTypes.object,
  processDataSource: PropTypes.func,
  processing: PropTypes.bool,
  refetchRun: PropTypes.func,
};

export default GDSNDataSourceManufacturersTab;
